import React, { useMemo } from 'react';
import * as THREE from 'three';
import { FOUP } from '../FOUP/FOUP';
import { resolveKitFoupScale } from '../types';
import { useRailElevation } from '../../railElevationContext';

const lightBlueGlassMat = new THREE.MeshPhysicalMaterial({
  color: '#38BDF8',
  transmission: 0.88,
  opacity: 0.48,
  transparent: true,
  roughness: 0.08,
  metalness: 0.12,
  ior: 1.52,
  clearcoat: 0.3,
  clearcoatRoughness: 0.1,
});

const smokedBlackGlassMat = new THREE.MeshPhysicalMaterial({
  color: '#0F172A',
  transmission: 0.82,
  opacity: 0.50,
  transparent: true,
  roughness: 0.12,
  metalness: 0.18,
  ior: 1.50,
  clearcoat: 0.25,
  clearcoatRoughness: 0.12,
});

export type GateStationLifterProps = {
  entry: boolean;
  active: boolean;
  gateHoldsFoup: boolean;
  progress01?: number;
  status?: string;
};

/**
 * Calculates vertical Y positions for lifter carriage and FOUP based on transfer progress.
 *
 * Flow:
 * - Entry (PICKUP): Lower FOUP Position -> Lifter Picks FOUP -> Moves Up to Overhead Track -> Hands off to OHT -> OHT Departs
 * - Exit (DROP): OHT Arrives at Track -> Lifter Receives FOUP at Track -> Moves Down to Floor Level
 */
export function resolveGateLifterMotion(
  progress01: number = 0,
  role: 'entry' | 'exit',
  active: boolean,
  gateHoldsFoup: boolean,
  trackElevationY: number = 30.0
): {
  carriageY: number;
  foupVisible: boolean;
  carriageLedColor: string;
  shuttleZ: number;
} {
  const BOTTOM_Y = 2.0;    // Lower level transfer position inside container
  // Top OHT handoff interface level:
  // Positioned slightly above the lifter crown frame (which is at trackElevationY + 1.56),
  // placing the FOUP at trackElevationY + 2.24 to directly align with the top deck of the OHT vehicle on the track.
  const TOP_Y = trackElevationY + 1.78;
  const STROKE = TOP_Y - BOTTOM_Y;
  const extendDist = 5.2;

  // Active / Ready / Idle status colors
  const activeColor = role === 'entry' ? '#00E5FF' : '#38BDF8';
  const readyColor = '#22C55E';
  const idleColor = '#475569';

  if (!active) {
    // Static / Idle State
    return {
      carriageY: BOTTOM_Y,
      foupVisible: gateHoldsFoup,
      carriageLedColor: gateHoldsFoup ? readyColor : idleColor,
      shuttleZ: 0,
    };
  }

  const p = Math.min(1, Math.max(0, progress01));

  if (role === 'entry') {
    // ──────── ENTRY TRANSFER (PICKUP) ────────
    // 1. Carriage ascends with FOUP from floor to top track (0.00..0.40)
    // 2. Carriage tray extends horizontally to track stop node under OHT (0.40..0.70)
    // 3. OHT receives FOUP at 0.70..0.80; tray holds at track (0.70..0.80)
    // 4. Empty tray retracts back into carriage (0.80..0.88)
    // 5. Empty carriage & tray descends with animation back down to floor (0.88..1.00)
    let liftFrac = 0;
    let shuttleZ = 0;
    let foupVisible = false;

    if (p < 0.40) {
      const u = p / 0.40;
      liftFrac = 0.5 - 0.5 * Math.cos(u * Math.PI);
      shuttleZ = 0;
      foupVisible = true;
    } else if (p < 0.75) {
      liftFrac = 1.0;
      const u = (p - 0.40) / 0.35;
      shuttleZ = (0.5 - 0.5 * Math.cos(u * Math.PI)) * extendDist;
      foupVisible = true; // FOUP securely held on lifter tray until OHT takes ownership at 0.75
    } else if (p < 0.80) {
      liftFrac = 1.0;
      shuttleZ = extendDist;
      foupVisible = false; // Picked by OHT at 0.75; OHT vehicle renders drawBox
    } else if (p < 0.88) {
      liftFrac = 1.0;
      const u = (p - 0.80) / 0.08;
      shuttleZ = (1 - (0.5 - 0.5 * Math.cos(u * Math.PI))) * extendDist;
      foupVisible = false;
    } else {
      shuttleZ = 0;
      const u = (p - 0.88) / 0.12;
      // Smooth descent of empty carriage & tray back down to floor level (BOTTOM_Y)
      liftFrac = 1.0 - (0.5 - 0.5 * Math.cos(u * Math.PI));
      foupVisible = false;
    }

    const carriageY = BOTTOM_Y + liftFrac * STROKE;

    return {
      carriageY,
      foupVisible,
      carriageLedColor: activeColor,
      shuttleZ,
    };
  } else {
    // ──────── EXIT TRANSFER (DROP) ────────
    // 1. Carriage at top, empty tray extends horizontally to track stop node under OHT (0.00..0.15)
    // 2. Receives FOUP from OHT at 0.15; tray retracts into carriage with FOUP (0.15..0.45)
    // 3. Vertical Carriage descends with FOUP to floor level (0.45..0.88)
    // 4. Rests securely at floor level (0.88..1.00)
    let dropFrac = 0; // 0 = top, 1 = bottom
    let shuttleZ = 0;
    let foupVisible = false;

    if (p < 0.15) {
      const u = p / 0.15;
      shuttleZ = (0.5 - 0.5 * Math.cos(u * Math.PI)) * extendDist;
      dropFrac = 0;
      foupVisible = false;
    } else if (p < 0.45) {
      const u = (p - 0.15) / 0.30;
      shuttleZ = (1 - (0.5 - 0.5 * Math.cos(u * Math.PI))) * extendDist;
      dropFrac = 0;
      foupVisible = true;
    } else if (p < 0.88) {
      shuttleZ = 0;
      const u = (p - 0.45) / 0.43;
      dropFrac = 0.5 - 0.5 * Math.cos(u * Math.PI);
      foupVisible = true;
    } else {
      shuttleZ = 0;
      dropFrac = 1.0;
      foupVisible = true;
    }

    const carriageY = TOP_Y - dropFrac * STROKE;

    return {
      carriageY,
      foupVisible,
      carriageLedColor: activeColor,
      shuttleZ,
    };
  }
}

export const GateStationLifter3D: React.FC<GateStationLifterProps> = ({
  entry,
  active,
  gateHoldsFoup,
  progress01 = 0,
}) => {
  const dynamicRailY = useRailElevation();
  const trackElevationY = Math.max(30.0, dynamicRailY ?? 32.0);

  const role: 'entry' | 'exit' = entry ? 'entry' : 'exit';
  const { carriageY, foupVisible, carriageLedColor, shuttleZ } =
    resolveGateLifterMotion(progress01, role, active, gateHoldsFoup, trackElevationY);

  // Lifter Structure Dimensions
  const SHAFT_W = 6.8;   // Along track X
  const SHAFT_D = 6.8;   // Cross-track Z
  const BASE_FLOOR_Y = 1.0;
  const TOP_DECK_Y = 20.0; // Top of container enclosure
  const TOP_TRACK_Y = trackElevationY + 1.2; // Overhead track rail level (matches top surface of track)
  const SHAFT_H = TOP_TRACK_Y - BASE_FLOOR_Y; // Full height from floor to overhead track
  const SHAFT_MID_Y = BASE_FLOOR_Y + SHAFT_H / 2;

  // Extend precision chrome guide shafts up through the carriage bearings at top stop
  const GUIDE_SHAFT_TOP_Y = trackElevationY + 2.15;
  const GUIDE_SHAFT_H = GUIDE_SHAFT_TOP_Y - BASE_FLOOR_Y;
  const GUIDE_SHAFT_MID_Y = BASE_FLOOR_Y + GUIDE_SHAFT_H / 2;

  const halfW = SHAFT_W / 2; // 3.4
  const halfD = SHAFT_D / 2; // 3.4
  const postSize = 0.20; // Sleek thin cleanroom frame columns

  // Materials - Cleanroom Brushed Silver Metal Frame
  const frameMat = useMemo(
    () => ({
      color: '#CBD5E1',
      metalness: 0.75,
      roughness: 0.28,
      envMapIntensity: 1.1,
    }),
    []
  );
  const chromeMat = useMemo(
    () => ({
      color: '#F8FAFC',
      metalness: 0.95,
      roughness: 0.1,
      envMapIntensity: 2.0,
    }),
    []
  );
  const darkAnodizedMat = useMemo(
    () => ({
      color: '#1E293B',
      metalness: 0.65,
      roughness: 0.35,
    }),
    []
  );
  const safetyYellow = useMemo(
    () => ({
      color: '#EAB308',
      metalness: 0.28,
      roughness: 0.32,
    }),
    []
  );

  const foupScale = resolveKitFoupScale(SHAFT_W, SHAFT_H, SHAFT_D);

  return (
    <group name="GateStationFOUPTransferLifter" position={[0, 0, -5.2]}>
      {/* ──────────────── 1. BASE ANCHOR & LOWER TRANSFER NEST (FLOOR Y=1.0) ──────────────── */}
      {/* Lower structural mounting plate securely resting on container floor */}
      <mesh position={[0, BASE_FLOOR_Y + 0.15, 0]} castShadow receiveShadow>
        <boxGeometry args={[SHAFT_W + 0.6, 0.3, SHAFT_D + 0.6]} />
        <meshStandardMaterial {...frameMat} />
      </mesh>

      {/* Lower Transfer Nest Buffer Base */}
      <mesh position={[0, BASE_FLOOR_Y + 0.45, 0]} castShadow receiveShadow>
        <boxGeometry args={[SHAFT_W - 0.6, 0.3, SHAFT_D - 0.6]} />
        <meshStandardMaterial {...darkAnodizedMat} />
      </mesh>

      {/* Lower Transfer Alignment Nest Plate at Y = 2.0 */}
      <mesh position={[0, 1.9, 0]} receiveShadow>
        <boxGeometry args={[5.2, 0.2, 5.2]} />
        <meshStandardMaterial {...frameMat} />
      </mesh>

      {/* 3 Kinematic Locating Alignment Pins at Lower Nest */}
      {(
        [
          [0, 2.08, -1.3],
          [-1.1, 2.08, 1.0],
          [1.1, 2.08, 1.0],
        ] as const
      ).map(([px, py, pz], i) => (
        <mesh key={`lower-nest-pin-${i}`} position={[px, py, pz]} castShadow>
          <cylinderGeometry args={[0.16, 0.22, 0.25, 16]} />
          <meshStandardMaterial {...chromeMat} />
        </mesh>
      ))}

      {/* ──────────────── 2. FULL-HEIGHT 4 CORNER VERTICAL MAST COLUMNS (FLOOR TO OVERHEAD TRACK) ──────────────── */}
      {([-1, 1] as const).flatMap((sx) =>
        ([-1, 1] as const).map((sz) => (
          <mesh
            key={`lifter-col-${sx}-${sz}`}
            position={[sx * (halfW - postSize / 2), SHAFT_MID_Y, sz * (halfD - postSize / 2)]}
            castShadow
            receiveShadow
          >
            <boxGeometry args={[postSize, SHAFT_H, postSize]} />
            <meshStandardMaterial {...frameMat} />
          </mesh>
        ))
      )}

      {/* ──────────────── 3. DUAL PRECISION CHROME LINEAR GUIDE SHAFTS (FULL HEIGHT TO TRACK) ──────────────── */}
      {([-1, 1] as const).map((sz) => (
        <group key={`guide-shaft-group-${sz}`}>
          <mesh
            key={`guide-shaft-${sz}`}
            position={[0, GUIDE_SHAFT_MID_Y, sz * (halfD - 0.7)]}
            castShadow
          >
            <cylinderGeometry args={[0.12, 0.12, GUIDE_SHAFT_H, 16]} />
            <meshStandardMaterial {...chromeMat} />
          </mesh>
          <mesh
            key={`guide-shaft-cap-${sz}`}
            position={[0, GUIDE_SHAFT_TOP_Y - 0.08, sz * (halfD - 0.7)]}
            castShadow
          >
            <cylinderGeometry args={[0.20, 0.20, 0.16, 16]} />
            <meshStandardMaterial {...frameMat} />
          </mesh>
        </group>
      ))}

      {/* ──────────────── 5. STRUCTURAL CROSS-TIE COLLARS AT TIERS ──────────────── */}
      {[10.5, TOP_DECK_Y, (TOP_DECK_Y + TOP_TRACK_Y) * 0.5].map((tierY, tIdx) => (
        <group key={`cross-tier-${tIdx}`} position={[0, tierY, 0]}>
          <mesh position={[0, 0, -halfD + postSize / 2]}>
            <boxGeometry args={[SHAFT_W, 0.18, postSize]} />
            <meshStandardMaterial {...frameMat} />
          </mesh>
          <mesh position={[0, 0, halfD - postSize / 2]}>
            <boxGeometry args={[SHAFT_W, 0.18, postSize]} />
            <meshStandardMaterial {...frameMat} />
          </mesh>
          <mesh position={[-halfW + postSize / 2, 0, 0]}>
            <boxGeometry args={[postSize, 0.18, SHAFT_D]} />
            <meshStandardMaterial {...frameMat} />
          </mesh>
          <mesh position={[halfW - postSize / 2, 0, 0]}>
            <boxGeometry args={[postSize, 0.18, SHAFT_D]} />
            <meshStandardMaterial {...frameMat} />
          </mesh>
        </group>
      ))}

      {/* ──────────────── 6. TRANSPARENT LIGHT-BLUE & SMOKED BLACK GLASS PANELS (FULL-HEIGHT LIFTER TOWER) ──────────────── */}
      {/* Left & Right Glass Panels (±X Faces) — Light Blue Cleanroom Glass */}
      {([-1, 1] as const).map((sx) => (
        <mesh
          key={`lifter-glass-x-${sx}`}
          position={[sx * (halfW - 0.04), SHAFT_MID_Y, 0]}
          material={lightBlueGlassMat}
          castShadow={false}
          receiveShadow
        >
          <boxGeometry args={[0.06, SHAFT_H - 0.6, SHAFT_D - postSize * 2]} />
        </mesh>
      ))}

      {/* Rear Glass Panel (-Z Face) — Smoked Light-Black Cleanroom Glass */}
      <mesh
        position={[0, SHAFT_MID_Y, -halfD + 0.04]}
        material={smokedBlackGlassMat}
        castShadow={false}
        receiveShadow
      >
        <boxGeometry args={[SHAFT_W - postSize * 2, SHAFT_H - 0.6, 0.06]} />
      </mesh>

      {/* Front Glass Panel (+Z Face) — Light Blue Cleanroom Glass */}
      <mesh
        position={[0, BASE_FLOOR_Y + (SHAFT_H - 4.2) * 0.5, halfD - 0.04]}
        material={lightBlueGlassMat}
        castShadow={false}
        receiveShadow
      >
        <boxGeometry args={[SHAFT_W - postSize * 2, SHAFT_H - 4.2, 0.06]} />
      </mesh>

      {/* ──────────────── 7. SINGLE UNIFIED MOVING LIFTER CARRIAGE + TELESCOPIC TRAY ──────────────── */}
      <group position={[0, carriageY, 0]} name="LifterCarriageHolder">
        {/* Main Carriage Base Frame */}
        <mesh position={[0, 0.1, 0]} castShadow receiveShadow>
          <boxGeometry args={[5.2, 0.2, 5.2]} />
          <meshStandardMaterial color="#1E293B" metalness={0.75} roughness={0.28} />
        </mesh>

        {/* Lower Structural Support Ribs */}
        <mesh position={[0, -0.15, 0]} castShadow>
          <boxGeometry args={[4.6, 0.3, 4.6]} />
          <meshStandardMaterial {...darkAnodizedMat} />
        </mesh>

        {/* Dual Side Linear Bearing Pillow Blocks gripping Chrome Guide Rails */}
        {([-1, 1] as const).map((sz) => (
          <mesh
            key={`carriage-bearing-${sz}`}
            position={[0, 0.05, sz * (halfD - 0.7)]}
            castShadow
          >
            <boxGeometry args={[0.8, 0.6, 0.7]} />
            <meshStandardMaterial color="#94A3B8" metalness={0.85} roughness={0.2} />
          </mesh>
        ))}

        {/* Horizontal Telescopic Guide Rails on Carriage */}
        {([-1, 1] as const).map((sx) => (
          <mesh
            key={`carriage-shuttle-rail-${sx}`}
            position={[sx * (halfW * 0.65), 0.22, shuttleZ * 0.5]}
          >
            <boxGeometry args={[0.1, 0.08, 4.4 + Math.abs(shuttleZ)]} />
            <meshStandardMaterial {...chromeMat} />
          </mesh>
        ))}

        {/* Single Motorized Shuttle Tray (Translates along Z by shuttleZ) */}
        <group position={[0, 0.24, shuttleZ]} name="CarriageShuttleTray">
          {/* Main Shuttle Plate */}
          <mesh castShadow receiveShadow>
            <boxGeometry args={[4.8, 0.14, 4.8]} />
            <meshStandardMaterial {...frameMat} />
          </mesh>

          {/* LED Perimeter Status Glow Strip */}
          <mesh position={[0, 0.02, 2.42]}>
            <boxGeometry args={[4.9, 0.04, 0.04]} />
            <meshStandardMaterial
              color={carriageLedColor}
              emissive={carriageLedColor}
              emissiveIntensity={1.0}
              toneMapped={false}
            />
          </mesh>

          {/* Safety Yellow Mechanical Clamping Cradle Arms */}
          {([-1, 1] as const).map((sx) => (
            <group key={`shuttle-cradle-${sx}`} position={[sx * 2.15, 0.22, 0]}>
              <mesh castShadow>
                <boxGeometry args={[0.2, 0.36, 3.4]} />
                <meshStandardMaterial {...safetyYellow} />
              </mesh>
              {[-1.5, 1.5].map((fz) => (
                <mesh key={`finger-${fz}`} position={[sx * -0.07, 0.14, fz]} castShadow>
                  <boxGeometry args={[0.14, 0.14, 0.32]} />
                  <meshStandardMaterial {...safetyYellow} />
                </mesh>
              ))}
            </group>
          ))}

          {/* 3 Kinematic Alignment Locating Pins */}
          {(
            [
              [0, 0.16, -1.2],
              [-1.0, 0.16, 0.9],
              [1.0, 0.16, 0.9],
            ] as const
          ).map(([px, py, pz], i) => (
            <mesh key={`tray-pin-${i}`} position={[px, py, pz]} castShadow>
              <cylinderGeometry args={[0.12, 0.16, 0.2, 16]} />
              <meshStandardMaterial {...chromeMat} />
            </mesh>
          ))}

          {/* Actual Semiconductor FOUP seated directly on the single moving tray */}
          {foupVisible && (
            <FOUP
              position={[0, 0.22, 0]}
              scale={foupScale}
            />
          )}
        </group>
      </group>

      {/* ──────────────── 10. TOP TRACK CONNECTION OUTRIGGER GIRDERS & CLAMPING SADDLES ──────────────── */}
      <group position={[0, TOP_TRACK_Y, 0]}>
        {/* Full-Length Continuous Side Girders spanning from Rear Columns (Z=-halfD) to Track (Z=5.2) */}
        {([-1, 1] as const).map((sx) => (
          <group key={`top-girder-${sx}`} position={[sx * (halfW - postSize / 2), 0.18, 1.3]}>
            <mesh castShadow receiveShadow>
              <boxGeometry args={[postSize, 0.36, 9.4]} />
              <meshStandardMaterial {...frameMat} />
            </mesh>
            <mesh position={[0, 0.20, 0]}>
              <boxGeometry args={[postSize * 1.3, 0.06, 9.4]} />
              <meshStandardMaterial {...frameMat} />
            </mesh>
          </group>
        ))}

        {/* Rear Crown Cross Beam (Connecting rear columns at Z = -halfD) */}
        <mesh position={[0, 0.18, -halfD + postSize / 2]} castShadow receiveShadow>
          <boxGeometry args={[SHAFT_W, 0.36, postSize]} />
          <meshStandardMaterial {...frameMat} />
        </mesh>

        {/* Front Crown Cross Beam (Connecting front columns at Z = halfD) */}
        <mesh position={[0, 0.18, halfD - postSize / 2]} castShadow receiveShadow>
          <boxGeometry args={[SHAFT_W, 0.36, postSize]} />
          <meshStandardMaterial {...frameMat} />
        </mesh>

        {/* 4 Corner Column End Caps */}
        {([-1, 1] as const).flatMap((sx) =>
          ([-1, 1] as const).map((sz) => (
            <mesh
              key={`corner-cap-${sx}-${sz}`}
              position={[sx * (halfW - postSize / 2), 0.38, sz * (halfD - postSize / 2)]}
              castShadow
            >
              <boxGeometry args={[postSize * 1.3, 0.08, postSize * 1.3]} />
              <meshStandardMaterial {...darkAnodizedMat} />
            </mesh>
          ))
        )}

        {/* Transverse Cross Beams under rail at Z=5.2 */}
        {[5.2 - 0.9, 5.2 + 0.9].map((tz, tIdx) => (
          <mesh key={`top-cross-${tIdx}`} position={[0, 0.18, tz]} castShadow>
            <boxGeometry args={[SHAFT_W + 0.8, 0.36, 0.3]} />
            <meshStandardMaterial {...darkAnodizedMat} />
          </mesh>
        ))}

        {/* Track Clamping Saddle Shoes rigidly connecting to overhead rail at Z=5.2 */}
        {([-1, 1] as const).flatMap((sx) =>
          [5.2 - 0.6, 5.2 + 0.6].map((tz, sIdx) => (
            <group key={`track-shoe-${sx}-${sIdx}`} position={[sx * (halfW * 0.7), 0.45, tz]}>
              <mesh castShadow>
                <boxGeometry args={[0.4, 0.5, 0.4]} />
                <meshStandardMaterial {...darkAnodizedMat} />
              </mesh>
              <mesh position={[0, 0.28, 0]}>
                <boxGeometry args={[0.5, 0.12, 0.5]} />
                <meshStandardMaterial {...frameMat} />
              </mesh>
              {[-0.12, 0.12].map((bx) => (
                <mesh key={`bolt-${bx}`} position={[bx, 0.36, 0]}>
                  <cylinderGeometry args={[0.04, 0.04, 0.08, 8]} />
                  <meshStandardMaterial {...chromeMat} />
                </mesh>
              ))}
            </group>
          ))
        )}
      </group>
    </group>
  );
};

export default React.memo(GateStationLifter3D);
